import { useState, FormEvent } from "react";
import FileDropzoneWithPreview from "src/widgets/help/fileDropzoneWithPreview/FileDropzoneWithPreview";
import supportCss from "src/widgets/help/supportModalShared.module.scss";
import css from "../SupportTicketPage.module.scss";
import { formatMb } from "../utils/supportTicketUtils";

const MAX_FILES = 10;
const MAX_TOTAL_SIZE_BYTES = 15000 * 1024; // 15 000 Кб ≈ 14.65 Мб

type MessageFormProps = {
    isClosed: boolean;
    isSending: boolean;
    sendError: string | null;
    onSubmit: (message: string, files: File[]) => Promise<void>;
};

export default function MessageForm({
    isClosed,
    isSending,
    sendError,
    onSubmit,
}: MessageFormProps) {
    const [newMessage, setNewMessage] = useState<string>("");
    const [files, setFiles] = useState<File[]>([]);
    const [filesErrorText, setFilesErrorText] = useState<string | null>(null);

    const totalBytes = files.reduce((acc, f) => acc + (f?.size ?? 0), 0);
    const percent = Math.min(100, Math.round((totalBytes / MAX_TOTAL_SIZE_BYTES) * 100));
    const exceedLimit = totalBytes > MAX_TOTAL_SIZE_BYTES;

    function onAddFiles(fileList: FileList | File[]) {
        if (!fileList) return;
        const next = Array.isArray(fileList) ? fileList : Array.from(fileList);
        const newTotal = files.length + next.length;
        const merged = [...files, ...next];
        const limited = merged.slice(0, MAX_FILES);
        setFiles(limited);
        setFilesErrorText(
            newTotal > MAX_FILES ? `Можно прикрепить не более ${MAX_FILES} файлов` : null,
        );
    }

    function removeFile(idx: number) {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
        setFilesErrorText(null);
    }

    async function handleSend(e: FormEvent) {
        e.preventDefault();
        if (isClosed) return;
        const text = newMessage.trim();
        if (!text || exceedLimit) return;

        try {
            await onSubmit(text, files);
            setNewMessage("");
            setFiles([]);
            setFilesErrorText(null);
        } catch (error) {
            // Ошибка обрабатывается в родительском компоненте
        }
    }

    return (
        <form
            className={`${css.chatForm} ${isClosed ? css.chatFormDisabled : ""}`}
            onSubmit={handleSend}
        >
            <label
                className={css.chatLabel}
                htmlFor="support-ticket-message-input"
            >
                Новое сообщение в поддержку
            </label>
            <textarea
                id="support-ticket-message-input"
                className={css.chatTextarea}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        // Ctrl+Enter, Cmd+Enter или Shift+Enter - перенос строки
                        if (e.ctrlKey || e.metaKey || e.shiftKey) {
                            // Явно вставляем перенос строки
                            e.preventDefault();
                            const textarea = e.currentTarget;
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const text = newMessage;
                            const newText = text.substring(0, start) + "\n" + text.substring(end);
                            setNewMessage(newText);
                            // Восстанавливаем позицию курсора после переноса
                            setTimeout(() => {
                                textarea.selectionStart = textarea.selectionEnd = start + 1;
                            }, 0);
                            return;
                        }
                        // Enter без модификаторов - отправка сообщения
                        e.preventDefault();
                        const syntheticEvent = {
                            preventDefault: () => {},
                        } as React.FormEvent<HTMLFormElement>;
                        handleSend(syntheticEvent);
                    }
                }}
                placeholder="Напишите сообщение..."
                disabled={isClosed}
            />
            <div className={supportCss.filesBlock}>
                <div className={supportCss.filesHeader}>
                    <span className={supportCss.label}>
                        Прикрепить файлы:
                    </span>
                    <div className={supportCss.progress}>
                        <div className={supportCss.progressTrack}>
                            <div
                                className={`${supportCss.progressFill} ${
                                    exceedLimit ? supportCss.over : ""
                                }`}
                                style={{
                                    width: `${percent}%`,
                                }}
                            />
                        </div>
                        <span className={supportCss.progressText}>
                            {formatMb(totalBytes)} Мб из 14.65 Мб ({percent}%)
                        </span>
                    </div>
                </div>
                {exceedLimit && (
                    <span className={supportCss.error}>
                        Превышен общий лимит прикреплённых файлов
                    </span>
                )}
                {filesErrorText && (
                    <span className={supportCss.error}>
                        {filesErrorText}
                    </span>
                )}
                <FileDropzoneWithPreview
                    files={files}
                    onAddFiles={onAddFiles}
                    onRemoveFile={removeFile}
                    disabled={isClosed || files.length >= MAX_FILES}
                    previewsPerRow={10}
                />
            </div>
            <div className={css.chatActions}>
                {sendError && (
                    <span className={css.chatError}>
                        {sendError}
                    </span>
                )}
                <button
                    type="submit"
                    className={css.sendButton}
                    disabled={
                        isClosed ||
                        isSending ||
                        !newMessage.trim() ||
                        exceedLimit
                    }
                >
                    {isSending ? "Отправка..." : "Отправить"}
                </button>
            </div>
        </form>
    );
}


